import classNames from 'classnames'
import { useEffect, useRef } from 'react'
import * as styles from './particle-field.css.ts'

/**
 * Decorative animated starfield rendered to a `<canvas>`. Each particle drifts
 * slowly, twinkles (sinusoidal opacity), and carries a soft radial glow.
 *
 * This is implemented directly against the Canvas 2D API rather than pulling in
 * a particle library (e.g. tsParticles): the effect is a few dozen lines, needs
 * no runtime configuration surface, and avoids adding a sizable dependency to a
 * security-focused, self-hosted product for what is purely background polish.
 */

// Brand palette for the particles. The Canvas 2D `fillStyle` needs literal
// color strings, so these cannot be the theme's CSS-variable tokens.
const PARTICLE_COLORS = ['#ff006c', '#c935ff', '#ff8a3d', '#00bbea', '#f55a4f', '#6000fe'] as const

// One particle per this many square pixels of canvas, before the `density` prop.
const AREA_PER_PARTICLE = 9000

// Dot radius is chosen uniformly in this range (CSS pixels).
const MIN_RADIUS = 0.4
const MAX_RADIUS = 2.8

// Per-axis drift is chosen uniformly in [-MAX, +MAX] CSS pixels per frame.
const MAX_DRIFT_VELOCITY = 0.075

// Base (pre-twinkle) opacity is chosen uniformly in this range.
const MIN_OPACITY = 0.2
const MAX_OPACITY = 1

// Twinkle: opacity = base * (PULSE_CENTER + PULSE_AMPLITUDE * sin(phase)), with
// the phase advancing PULSE_SPEED radians per frame from a random start. The
// center/amplitude pair makes opacity swing across the full 0..1 range.
const PULSE_CENTER = 0.5
const PULSE_AMPLITUDE = 0.5
const PULSE_SPEED = 0.02

// A particle wraps to the opposite edge only once this far past a boundary, so
// it never visibly pops in or out at the edge.
const WRAP_MARGIN = 10

// The glow halo is drawn this many times larger than the solid dot.
const GLOW_RADIUS_MULTIPLIER = 4

const TWO_PI = Math.PI * 2
const TRANSPARENT = 'rgba(0, 0, 0, 0)'

type Particle = {
  x: number
  y: number
  radius: number
  velocityX: number
  velocityY: number
  color: string
  opacity: number
  pulsePhase: number
}

const randomBetween = (min: number, max: number) => min + Math.random() * (max - min)

type ParticleFieldProps = {
  /** Multiplies the particle count relative to canvas area (1 = default). */
  density?: number
  /** Scales the opacity of every particle (1 = default). */
  intensity?: number
  className?: string
}

export const ParticleField: React.FC<ParticleFieldProps> = ({ density = 1, intensity = 1, className }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) {
      return
    }
    const context = canvas.getContext('2d')
    if (!context) {
      return
    }

    let animationFrameId: number
    let width = 0
    let height = 0
    let particles: Particle[] = []

    // Match the backing store to the canvas's CSS box and device pixel ratio,
    // then (re)seed particles for the new area. Runs on mount and on resize.
    const resizeAndSeed = () => {
      const devicePixelRatio = window.devicePixelRatio ?? 1
      const bounds = canvas.getBoundingClientRect()
      width = bounds.width
      height = bounds.height
      canvas.width = width * devicePixelRatio
      canvas.height = height * devicePixelRatio
      // Draw in CSS pixels; this transform scales up to the backing store.
      context.setTransform(devicePixelRatio, 0, 0, devicePixelRatio, 0, 0)

      const particleCount = Math.floor(((width * height) / AREA_PER_PARTICLE) * density)
      particles = Array.from({ length: particleCount }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        radius: randomBetween(MIN_RADIUS, MAX_RADIUS),
        velocityX: randomBetween(-MAX_DRIFT_VELOCITY, MAX_DRIFT_VELOCITY),
        velocityY: randomBetween(-MAX_DRIFT_VELOCITY, MAX_DRIFT_VELOCITY),
        color: PARTICLE_COLORS[Math.floor(Math.random() * PARTICLE_COLORS.length)] ?? PARTICLE_COLORS[0],
        opacity: randomBetween(MIN_OPACITY, MAX_OPACITY),
        pulsePhase: Math.random() * TWO_PI,
      }))
    }

    const renderFrame = () => {
      context.clearRect(0, 0, width, height)

      for (const particle of particles) {
        particle.x += particle.velocityX
        particle.y += particle.velocityY
        particle.pulsePhase += PULSE_SPEED

        // Wrap around the edges to keep the field seamless.
        if (particle.x < -WRAP_MARGIN) {
          particle.x = width + WRAP_MARGIN
        }
        if (particle.x > width + WRAP_MARGIN) {
          particle.x = -WRAP_MARGIN
        }
        if (particle.y < -WRAP_MARGIN) {
          particle.y = height + WRAP_MARGIN
        }
        if (particle.y > height + WRAP_MARGIN) {
          particle.y = -WRAP_MARGIN
        }

        context.globalAlpha =
          (PULSE_CENTER + PULSE_AMPLITUDE * Math.sin(particle.pulsePhase)) * particle.opacity * intensity

        // Soft glow halo: a radial gradient fading from the particle color to
        // transparent.
        const glowRadius = particle.radius * GLOW_RADIUS_MULTIPLIER
        const glow = context.createRadialGradient(particle.x, particle.y, 0, particle.x, particle.y, glowRadius)
        glow.addColorStop(0, particle.color)
        glow.addColorStop(1, TRANSPARENT)
        context.fillStyle = glow
        context.beginPath()
        context.arc(particle.x, particle.y, glowRadius, 0, TWO_PI)
        context.fill()

        // Solid dot core.
        context.fillStyle = particle.color
        context.beginPath()
        context.arc(particle.x, particle.y, particle.radius, 0, TWO_PI)
        context.fill()
      }

      context.globalAlpha = 1
      animationFrameId = requestAnimationFrame(renderFrame)
    }

    resizeAndSeed()
    renderFrame()

    const resizeObserver = new ResizeObserver(resizeAndSeed)
    resizeObserver.observe(canvas)

    return () => {
      cancelAnimationFrame(animationFrameId)
      resizeObserver.disconnect()
    }
  }, [density, intensity])

  return <canvas ref={canvasRef} className={classNames(styles.canvas, className)} />
}
